import React, { useState, useCallback } from 'react';
import { Box, Skeleton } from '@mui/material';
import { getCrossOrigin } from '../utils/imageUtils';
import { useImageRetry } from '../hooks/useImageRetry';
import { sendImageErrorBeacon } from '../utils/imageErrorBeacon';
import { NoImagePlaceholder } from './NoImagePlaceholder';

interface ThumbnailImageProps {
  src: string | null;
  alt: string;
  maxWidth?: string | number;
  maxHeight?: string | number;
}

/**
 * Renders an artwork thumbnail with a skeleton placeholder while the image loads.
 * Fixes issue #989 where thumbnails appeared blank while network images fetched.
 * Enhanced with retry logic for transient CDN failures.
 * Shows NoImagePlaceholder when src is null or all retries are exhausted (#870).
 */
export const ThumbnailImage: React.FC<ThumbnailImageProps> = ({
  src,
  alt,
  maxWidth = '100%',
  maxHeight = '100%',
}) => {
  const [loaded, setLoaded] = useState(false);

  const onAllRetriesFailed = useCallback((details: { originalUrl: string; retryCount: number }) => {
    sendImageErrorBeacon({
      url: details.originalUrl,
      retryCount: details.retryCount,
    });
  }, []);

  const {
    currentSrc,
    hasExhaustedRetries,
    handleError: retryHandleError,
    handleLoad: retryHandleLoad,
  } = useImageRetry({
    src: src || '',
    onAllRetriesFailed,
  });

  if (!src || hasExhaustedRetries) {
    return <NoImagePlaceholder variant="thumbnail" />;
  }

  const isReady = loaded;

  const handleLoad = () => {
    setLoaded(true);
    retryHandleLoad();
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {!isReady && (
        <Skeleton
          data-testid="thumbnail-skeleton"
          variant="rectangular"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: 1,
          }}
        />
      )}
      <Box
        component="img"
        crossOrigin={getCrossOrigin(currentSrc)}
        src={currentSrc}
        alt={alt}
        aria-hidden={isReady ? undefined : true}
        onLoad={handleLoad}
        onError={retryHandleError}
        sx={{
          maxWidth,
          maxHeight,
          objectFit: 'contain',
          display: isReady ? 'block' : 'none',
        }}
      />
    </Box>
  );
};
